/** Brief localized hit flash with glow burst, optional screen overlay, and camera shake. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { ensureGlowTexture } from '../../ui/CrispUtil';

export interface ImpactFlashConfig extends VfxPrimitiveConfig {
  flashRadius: number;
  flashDurationMs: number;
  flashScaleStart: number;
  flashScaleEnd: number;
  screenFlash: boolean;
  screenFlashAlpha: number;
  screenFlashDurationMs: number;
  screenFlashTint: number;
  shakeIntensity: number;
  shakeDurationMs: number;
}

const GLOW_POOL_SIZE = 4;

export class ImpactFlash extends VfxPrimitive<ImpactFlashConfig> {
  private textureKey!: string;
  private poolIndex = 0;
  private screenOverlay: Phaser.GameObjects.Rectangle | null = null;

  protected allocatePool(): void {
    this.textureKey = ensureGlowTexture(this.scene, this.config.flashRadius);
    for (let i = 0; i < GLOW_POOL_SIZE; i++) {
      this.createPoolImage(this.textureKey);
    }
    if (this.config.screenFlash) {
      const cam = this.scene.cameras.main;
      this.screenOverlay = this.scene.add
        .rectangle(cam.centerX, cam.centerY, cam.width, cam.height, this.config.screenFlashTint)
        .setAlpha(0)
        .setDepth(Number.MAX_SAFE_INTEGER)
        .setScrollFactor(0)
        .setVisible(false);
    }
  }

  play(x: number, y: number, depth: number): void {
    const { config } = this;

    // Activate next glow from pool (round-robin allows overlapping hits)
    const img = this.pool[this.poolIndex % this.pool.length];
    this.poolIndex++;
    this.scene.tweens.killTweensOf(img);
    this.activate(img, x, y, depth);
    img.setScale(config.flashScaleStart);

    this.scene.tweens.add({
      targets: img,
      scaleX: config.flashScaleEnd,
      scaleY: config.flashScaleEnd,
      alpha: 0,
      duration: config.flashDurationMs,
      ease: 'Quad.easeOut',
      onComplete: () => this.deactivate(img),
    });

    // Optional screen flash overlay
    if (config.screenFlash && this.screenOverlay) {
      this.scene.tweens.killTweensOf(this.screenOverlay);
      this.screenOverlay.setAlpha(config.screenFlashAlpha).setVisible(true);
      this.scene.tweens.add({
        targets: this.screenOverlay,
        alpha: 0,
        duration: config.screenFlashDurationMs,
        ease: 'Linear',
        onComplete: () => this.screenOverlay?.setVisible(false),
      });
    }

    // Optional camera shake
    if (config.shakeIntensity > 0 && config.shakeDurationMs > 0) {
      this.scene.cameras.main.shake(config.shakeDurationMs, config.shakeIntensity / 1000);
    }
  }

  /** No-op — flashes are inherently transient. */
  stop(): void {
    // Intentionally empty: impact flashes self-terminate via tween completion.
  }

  destroy(): void {
    if (this.screenOverlay) {
      this.scene.tweens.killTweensOf(this.screenOverlay);
      this.screenOverlay.destroy();
      this.screenOverlay = null;
    }
    super.destroy();
  }
}
